import Grid from "components/primitives/Grid";
import { Formik, Form } from "formik";
import { useCallback, useMemo } from "react";
import FormText from "components/Inputs/Form/FormText";
import { useTranslation } from "react-i18next";
import EyeIcon from "components/Icons/EyeIcon";
import EyeOffIcon from "components/Icons/EyeOffIcon";
import { PasswordInputSchema } from "./PasswordInput.schema";
import useTogglePasswordInputType from "hooks/useTogglePasswordInputType";
import SubmitAndCancelButtons from "../SubmitAndCancelButtons";
import ThemeProvider2024 from "components/MuiThemeProvider/ThemeProvider2024";

export interface PasswordInputProps {
  onReauthenticate(password: string): void | Promise<void>;
  onCancel(): void;
  errorCode?: string;
}

function PasswordInput(props: PasswordInputProps) {
  const { t } = useTranslation();
  const { onCancel, onReauthenticate, errorCode } = props;
  const { type, toggleType } = useTogglePasswordInputType();

  const handleSubmit = useCallback(
    (values: { password: string }) => onReauthenticate(values.password),
    [onReauthenticate]
  );

  const endAdornment = useMemo(() => {
    return (
      <ThemeProvider2024>
        {type === "password" ? (
          <EyeIcon
            onClick={toggleType}
            aria-label={t("settings.account_show_password_aria_label")}
            sx={{ color: "grey.100" }}
          />
        ) : (
          <EyeOffIcon
            onClick={toggleType}
            aria-label={t("settings.account_hide_password_aria_label")}
            sx={{ color: "grey.100" }}
          />
        )}
      </ThemeProvider2024>
    );
  }, [type, toggleType, t]);

  return (
    <Formik initialValues={{ password: "" }} onSubmit={handleSubmit} validationSchema={PasswordInputSchema}>
      {({ isSubmitting, dirty }) => (
        <Form>
          <Grid container spacing="24px" maxWidth={480}>
            <Grid size={12}>
              <FormText
                id="password-input"
                type={type}
                label={t("reauthentication_provider.password_input_label")}
                name="password"
                placeholder={t("reauthentication_provider.password_input_placeholder")}
                variant="standard"
                color="secondary"
                fullWidth
                error={!!errorCode}
                helperText={errorCode && t(errorCode, { context: "passwordOnly" })}
                slotProps={{
                  input: { endAdornment: endAdornment },
                }}
              />
            </Grid>
            <Grid size={12}>
              <SubmitAndCancelButtons
                submitText={t("global.next_button_text")}
                submitDisabled={!dirty || isSubmitting}
                onCancel={onCancel}
              />
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
}

export default PasswordInput;
